import type { Predicate, SearchDescriptor } from '@shared/core/search';
import { BE_DESCRIPTOR, type BusinessEntityDoc } from '@shared/core/search/entities/businessEntity';
import { type ReactNode, useCallback } from 'react';

import { EntityPicker } from '../components/EntityPicker';

interface BusinessEntityRef {
  readonly id: number;
  readonly name: string;
}

export interface BusinessEntityPickerProps {
  readonly filter: Predicate<BusinessEntityDoc>;
  readonly value: { readonly id: number; readonly name: string } | null | undefined;
  readonly onChange: (entity: BusinessEntityRef | null) => void;
  readonly label?: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly emptyText?: ReactNode;
  readonly isRequired?: boolean;
  readonly selectOnEnter?: boolean;
  readonly selectOnTab?: boolean;
  readonly 'data-testid'?: string;
  /** List the oldest entity first (relevance still first while typing), the order the Algolia picker it replaced listed. */
  readonly oldestFirst?: boolean;
}

/**
 * `BE_DESCRIPTOR` sorted oldest entity first on Typesense. The `businessEntities` collection's default sort is
 * `businessEntityId` desc (newest first); the release Algolia picker listed in index order, oldest first.
 */
export const BE_OLDEST_FIRST_DESCRIPTOR: SearchDescriptor<BusinessEntityDoc> = {
  ...BE_DESCRIPTOR,
  ranking: {
    ...BE_DESCRIPTOR.ranking,
    typesense: { ...BE_DESCRIPTOR.ranking.typesense, sortBy: '_text_match:desc,businessEntityId:asc' },
  },
};

const getLabel = (doc: BusinessEntityDoc): string => doc.name;

export function BusinessEntityPicker({ value, onChange, oldestFirst, ...rest }: BusinessEntityPickerProps) {
  const handleChange = useCallback(
    (id: number | null, doc: BusinessEntityDoc | null) => {
      onChange(id === null || doc === null ? null : { id: doc.businessEntityId, name: doc.name });
    },
    [onChange]
  );

  return (
    <EntityPicker<BusinessEntityDoc>
      {...rest}
      descriptor={oldestFirst ? BE_OLDEST_FIRST_DESCRIPTOR : BE_DESCRIPTOR}
      value={value?.id ?? null}
      selectedLabel={value?.name}
      onChange={handleChange}
      getLabel={getLabel}
    />
  );
}
